import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useParams } from "@tanstack/react-router";
import {
  AlertTriangle,
  CheckCircle2,
  Circle,
  Database,
  FileJson,
  FileSpreadsheet,
  Pause,
  Play,
  Rss,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { Eyebrow } from "@/components/dravian/eyebrow";
import {
  ingestion,
  sources as sourcesApi,
  type ApiIngestionRun,
  type ApiSource,
  type CsvPreview,
  type IngestionResult,
} from "@/lib/api";

export const Route = createFileRoute("/$workspace/data")({
  head: () => ({ meta: [{ title: "Data Ingestion — Dravian Horizon" }] }),
  component: DataPage,
});

function DataPage() {
  const { workspace: id } = useParams({ from: "/$workspace/data" });

  return (
    <div className="p-5 max-w-[1100px] mx-auto space-y-5">
      <div>
        <div className="label-eyebrow mb-1">Data Ingestion</div>
        <h2 className="text-[20px] font-semibold">Bring data into this workspace</h2>
        <p className="text-[12px] text-muted-foreground mt-1">
          Use these sources to seed entities, relationships, signals, and scenarios at scale. Each path applies entity resolution so duplicates merge automatically.
        </p>
      </div>

      <JsonImportPanel workspaceId={id} />
      <CsvImportPanel workspaceId={id} />
      <SourcesPanel workspaceId={id} />
      <RunsTable workspaceId={id} />
    </div>
  );
}

const CSV_TARGETS = [
  { v: "ignore", label: "Ignore" },
  { v: "name", label: "Name *" },
  { v: "type", label: "Type" },
  { v: "lat", label: "Latitude" },
  { v: "lng", label: "Longitude" },
  { v: "region", label: "Region" },
  { v: "color", label: "Color (hex)" },
  { v: "attribute", label: "Attribute" },
];

function CsvImportPanel({ workspaceId }: { workspaceId: string }) {
  const fileInput = useRef<HTMLInputElement | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [csvText, setCsvText] = useState<string>("");
  const [preview, setPreview] = useState<CsvPreview | null>(null);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [defaultType, setDefaultType] = useState<string>("custom");
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<IngestionResult | null>(null);
  const qc = useQueryClient();

  const previewMut = useMutation({
    mutationFn: (f: File) => ingestion.previewCsv(workspaceId, f),
    onSuccess: (p, f) => {
      setPreview(p);
      setMapping(p.suggested_mapping);
      // Cache the text so we can commit without re-uploading
      f.text().then(setCsvText);
    },
    onError: (e) => setError(e instanceof Error ? e.message : String(e)),
  });

  const commitMut = useMutation({
    mutationFn: () =>
      ingestion.commitCsv(workspaceId, {
        csv_text: csvText,
        mapping,
        default_type: defaultType,
      }),
    onSuccess: (r) => {
      setResult(r);
      setFile(null);
      setPreview(null);
      setCsvText("");
      setMapping({});
      qc.invalidateQueries({ queryKey: ["entities", workspaceId] });
      qc.invalidateQueries({ queryKey: ["relationships", workspaceId] });
      qc.invalidateQueries({ queryKey: ["workspaces"] });
    },
  });

  const onFile = (f: File) => {
    setError(null);
    setResult(null);
    setFile(f);
    previewMut.mutate(f);
  };

  const hasName = Object.values(mapping).includes("name");

  return (
    <section className="panel p-5 space-y-3">
      <div className="flex items-start gap-3">
        <FileSpreadsheet className="h-5 w-5 text-primary shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="text-[14px] font-semibold">CSV upload</div>
          <p className="text-[12px] text-muted-foreground mt-0.5">
            One row per entity. Map each column to a target field. Unmapped or "Attribute" columns
            are packed into the entity's <code>attributes</code>. Entity resolution merges duplicates by name.
          </p>
        </div>
      </div>

      {!preview && (
        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            const f = e.dataTransfer.files?.[0];
            if (f) onFile(f);
          }}
          className="rounded-md border border-dashed border-border bg-surface-elevated/30 p-6 text-center hover:border-primary/40 transition cursor-pointer"
          onClick={() => fileInput.current?.click()}
        >
          <Upload className="h-5 w-5 text-muted-foreground mx-auto mb-2" />
          <div className="text-[12px] text-foreground/90">
            Drop a CSV file or <span className="text-primary">browse</span>
          </div>
          <div className="text-[10px] text-muted-foreground mt-1">
            First row must be a header. UTF-8 or Latin-1 encoded.
          </div>
          <input
            ref={fileInput}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onFile(f);
              e.currentTarget.value = "";
            }}
          />
          {previewMut.isPending && (
            <div className="mt-2 text-[11px] text-muted-foreground">Parsing…</div>
          )}
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 rounded-md border border-risk-high/40 bg-risk-high/10 px-3 py-2 text-[12px] text-risk-high">
          <AlertTriangle className="h-3.5 w-3.5" /> {error}
        </div>
      )}

      {preview && (
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-[12px]">
            <FileSpreadsheet className="h-4 w-4 text-primary" />
            <span className="font-semibold">{file?.name}</span>
            <span className="text-muted-foreground">· {preview.row_count} rows</span>
          </div>

          <div>
            <Eyebrow>Column mapping</Eyebrow>
            <div className="rounded-md border border-border bg-surface overflow-hidden mt-1.5">
              <table className="w-full text-[12px]">
                <thead className="text-[10px] uppercase tracking-wider text-muted-foreground bg-surface-elevated/50">
                  <tr>
                    <th className="text-left px-3 py-2">Column</th>
                    <th className="text-left px-3 py-2">Sample</th>
                    <th className="text-left px-3 py-2">Maps to</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.columns.map((col) => (
                    <tr key={col} className="border-t border-border">
                      <td className="px-3 py-2 font-mono text-foreground/90">{col}</td>
                      <td className="px-3 py-2 text-muted-foreground">
                        {preview.sample_rows
                          .map((r) => r[col])
                          .filter(Boolean)
                          .slice(0, 2)
                          .join(", ") || "—"}
                      </td>
                      <td className="px-3 py-2">
                        <select
                          value={mapping[col] ?? "ignore"}
                          onChange={(e) => setMapping((m) => ({ ...m, [col]: e.target.value }))}
                          className="rounded border border-border bg-surface px-2 py-1 text-[12px] outline-none focus:border-primary/50"
                        >
                          {CSV_TARGETS.map((t) => (
                            <option key={t.v} value={t.v}>
                              {t.label}
                            </option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex items-end gap-3 flex-wrap">
            <div>
              <Eyebrow>Default type</Eyebrow>
              <input
                value={defaultType}
                onChange={(e) => setDefaultType(e.target.value)}
                placeholder="custom"
                className="mt-1.5 rounded border border-border bg-surface px-2 py-1 text-[12px] outline-none focus:border-primary/50"
              />
              <p className="text-[10px] text-muted-foreground mt-1">
                Used for rows where no <em>Type</em> column is mapped.
              </p>
            </div>
            <div className="ml-auto flex gap-2">
              <button
                onClick={() => {
                  setPreview(null);
                  setFile(null);
                  setCsvText("");
                  setMapping({});
                }}
                className="rounded border border-border bg-surface px-3 py-1.5 text-[12px] text-foreground/80 hover:bg-surface-elevated"
              >
                Cancel
              </button>
              <button
                onClick={() => commitMut.mutate()}
                disabled={!hasName || commitMut.isPending}
                className="rounded bg-primary text-primary-foreground px-4 py-1.5 text-[12px] font-semibold disabled:opacity-50"
                title={hasName ? undefined : "Map one column to Name first"}
              >
                {commitMut.isPending ? "Importing…" : `Import ${preview.row_count} rows`}
              </button>
            </div>
          </div>

          {!hasName && (
            <p className="text-[11px] text-risk-medium">
              At least one column must map to <strong>Name</strong> before you can import.
            </p>
          )}
        </div>
      )}

      {commitMut.isError && (
        <div className="flex items-center gap-2 rounded-md border border-risk-high/40 bg-risk-high/10 px-3 py-2 text-[12px] text-risk-high">
          <AlertTriangle className="h-3.5 w-3.5" /> {String(commitMut.error)}
        </div>
      )}

      {result && (
        <div className="rounded-md border border-risk-low/40 bg-risk-low/10 px-3 py-3 text-[12px]">
          <div className="flex items-center gap-2 mb-2 text-risk-low">
            <CheckCircle2 className="h-4 w-4" />
            <span className="font-semibold">CSV imported</span>
          </div>
          <ul className="text-[12px] space-y-0.5 text-foreground/90">
            <li>
              <span className="font-mono">{result.entities_created}</span> entities created,{" "}
              <span className="font-mono">{result.entities_resolved}</span> matched existing
            </li>
            {result.errors.length > 0 && (
              <li>
                <span className="font-mono text-risk-medium">{result.errors.length}</span> errors
              </li>
            )}
          </ul>
          {result.errors.length > 0 && (
            <details className="mt-3">
              <summary className="text-[11px] text-risk-medium cursor-pointer">
                Show errors
              </summary>
              <ul className="mt-2 space-y-0.5 text-[11px] text-risk-medium font-mono">
                {result.errors.slice(0, 20).map((e, i) => (
                  <li key={i}>· {e}</li>
                ))}
                {result.errors.length > 20 && <li>· +{result.errors.length - 20} more…</li>}
              </ul>
            </details>
          )}
        </div>
      )}
    </section>
  );
}

function JsonImportPanel({ workspaceId }: { workspaceId: string }) {
  const fileInput = useRef<HTMLInputElement | null>(null);
  const [pendingPayload, setPendingPayload] = useState<unknown | null>(null);
  const [pendingMeta, setPendingMeta] = useState<{ name: string; counts: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<IngestionResult | null>(null);
  const qc = useQueryClient();

  const importMut = useMutation({
    mutationFn: (payload: unknown) => ingestion.importJson(workspaceId, payload),
    onSuccess: (r) => {
      setResult(r);
      setPendingPayload(null);
      setPendingMeta(null);
      // Refresh every canvas-affecting query for this workspace
      qc.invalidateQueries({ queryKey: ["entities", workspaceId] });
      qc.invalidateQueries({ queryKey: ["relationships", workspaceId] });
      qc.invalidateQueries({ queryKey: ["signals", workspaceId] });
      qc.invalidateQueries({ queryKey: ["scenarios", workspaceId] });
      qc.invalidateQueries({ queryKey: ["workspaces"] });
    },
  });

  const onFile = async (file: File) => {
    setError(null);
    setResult(null);
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      if (!data || typeof data !== "object") {
        setError("File is not a valid JSON object.");
        return;
      }
      const e = Array.isArray(data.entities) ? data.entities.length : 0;
      const r = Array.isArray(data.relationships) ? data.relationships.length : 0;
      const s = Array.isArray(data.signals) ? data.signals.length : 0;
      const sc = Array.isArray(data.scenarios) ? data.scenarios.length : 0;
      if (e + r + s + sc === 0) {
        setError("File contains no entities, relationships, signals, or scenarios.");
        return;
      }
      setPendingPayload(data);
      setPendingMeta({
        name: file.name,
        counts: `${e} entities · ${r} relationships · ${s} signals · ${sc} scenarios`,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to read file");
    }
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) onFile(f);
  };

  return (
    <section className="panel p-5 space-y-3">
      <div className="flex items-start gap-3">
        <FileJson className="h-5 w-5 text-primary shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="text-[14px] font-semibold">JSON import</div>
          <p className="text-[12px] text-muted-foreground mt-0.5">
            Upload a file in the same shape as a workspace export. Resolution prevents duplicate entities. Reimporting is safe — repeated runs merge into existing rather than duplicate.
          </p>
        </div>
      </div>

      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDrop}
        className="rounded-md border border-dashed border-border bg-surface-elevated/30 p-6 text-center hover:border-primary/40 transition cursor-pointer"
        onClick={() => fileInput.current?.click()}
      >
        <Upload className="h-5 w-5 text-muted-foreground mx-auto mb-2" />
        <div className="text-[12px] text-foreground/90">
          Drop a JSON file or <span className="text-primary">browse</span>
        </div>
        <div className="text-[10px] text-muted-foreground mt-1">
          Exported by Horizon, or any object with{" "}
          <code className="text-foreground/75">entities</code>,{" "}
          <code className="text-foreground/75">relationships</code>,{" "}
          <code className="text-foreground/75">signals</code>,{" "}
          <code className="text-foreground/75">scenarios</code> arrays.
        </div>
        <input
          ref={fileInput}
          type="file"
          accept="application/json,.json"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) onFile(f);
            e.currentTarget.value = "";
          }}
        />
      </div>

      {error && (
        <div className="flex items-center gap-2 rounded-md border border-risk-high/40 bg-risk-high/10 px-3 py-2 text-[12px] text-risk-high">
          <AlertTriangle className="h-3.5 w-3.5" /> {error}
        </div>
      )}

      {pendingPayload !== null && pendingMeta && (
        <div className="rounded-md border border-primary/40 bg-primary/5 px-3 py-3 space-y-2">
          <div className="flex items-center gap-2 text-[12px]">
            <FileJson className="h-4 w-4 text-primary" />
            <span className="font-semibold">{pendingMeta.name}</span>
          </div>
          <div className="text-[11px] text-muted-foreground">{pendingMeta.counts}</div>
          <div className="flex justify-end gap-2">
            <button
              onClick={() => {
                setPendingPayload(null);
                setPendingMeta(null);
              }}
              className="rounded border border-border bg-surface px-3 py-1.5 text-[12px] text-foreground/80 hover:bg-surface-elevated"
            >
              Cancel
            </button>
            <button
              onClick={() => importMut.mutate(pendingPayload)}
              disabled={importMut.isPending}
              className="rounded bg-primary text-primary-foreground px-4 py-1.5 text-[12px] font-semibold disabled:opacity-50"
            >
              {importMut.isPending ? "Importing…" : "Import into workspace"}
            </button>
          </div>
        </div>
      )}

      {importMut.isError && (
        <div className="flex items-center gap-2 rounded-md border border-risk-high/40 bg-risk-high/10 px-3 py-2 text-[12px] text-risk-high">
          <AlertTriangle className="h-3.5 w-3.5" /> {String(importMut.error)}
        </div>
      )}

      {result && (
        <div className="rounded-md border border-risk-low/40 bg-risk-low/10 px-3 py-3 text-[12px]">
          <div className="flex items-center gap-2 mb-2 text-risk-low">
            <CheckCircle2 className="h-4 w-4" />
            <span className="font-semibold">Import complete</span>
          </div>
          <Eyebrow>Result</Eyebrow>
          <ul className="text-[12px] space-y-0.5 text-foreground/90 mt-1">
            <li>
              <span className="font-mono">{result.entities_created}</span> entities created,{" "}
              <span className="font-mono">{result.entities_resolved}</span> resolved into existing
            </li>
            <li>
              <span className="font-mono">{result.relationships_created}</span> relationships created
              {result.relationships_skipped > 0 && (
                <>
                  {" "}
                  · <span className="font-mono">{result.relationships_skipped}</span> skipped (unresolved
                  source/target)
                </>
              )}
            </li>
            <li>
              <span className="font-mono">{result.signals_created}</span> signals created
            </li>
            <li>
              <span className="font-mono">{result.scenarios_created}</span> scenarios created
            </li>
          </ul>
          {result.errors.length > 0 && (
            <details className="mt-3">
              <summary className="text-[11px] text-risk-medium cursor-pointer">
                {result.errors.length} error{result.errors.length === 1 ? "" : "s"}
              </summary>
              <ul className="mt-2 space-y-0.5 text-[11px] text-risk-medium font-mono">
                {result.errors.slice(0, 20).map((e, i) => (
                  <li key={i}>· {e}</li>
                ))}
                {result.errors.length > 20 && <li>· +{result.errors.length - 20} more…</li>}
              </ul>
            </details>
          )}
        </div>
      )}
    </section>
  );
}

type Kind = "rss" | "rest_api";
type HeaderPair = { key: string; value: string };

function SourcesPanel({ workspaceId }: { workspaceId: string }) {
  const qc = useQueryClient();
  const sources = useQuery({
    queryKey: ["sources", workspaceId],
    queryFn: () => sourcesApi.list(workspaceId),
    refetchInterval: 30_000,
  });

  const [adding, setAdding] = useState<null | Kind>(null);
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [interval, setInterval] = useState<number>(60);
  const [maxArticles, setMaxArticles] = useState<number>(25);
  const [method, setMethod] = useState<"GET" | "POST">("GET");
  const [headers, setHeaders] = useState<HeaderPair[]>([{ key: "", value: "" }]);
  const [createError, setCreateError] = useState<string | null>(null);

  const reset = () => {
    setAdding(null);
    setName("");
    setUrl("");
    setInterval(60);
    setMaxArticles(25);
    setMethod("GET");
    setHeaders([{ key: "", value: "" }]);
    setCreateError(null);
  };

  const createMut = useMutation({
    mutationFn: () => {
      const config: Record<string, unknown> = { url: url.trim() };
      if (adding === "rss") {
        config.max_articles = maxArticles;
      } else if (adding === "rest_api") {
        config.method = method;
        const hdrs: Record<string, string> = {};
        for (const h of headers) {
          if (h.key.trim() && h.value.trim()) {
            hdrs[h.key.trim()] = h.value.trim();
          }
        }
        if (Object.keys(hdrs).length > 0) config.headers = hdrs;
      }
      return sourcesApi.create(workspaceId, {
        name: name.trim(),
        kind: adding!,
        config,
        poll_interval_minutes: interval,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["sources", workspaceId] });
      reset();
    },
    onError: (e) => setCreateError(e instanceof Error ? e.message : String(e)),
  });

  const runMut = useMutation({
    mutationFn: (sourceId: string) => sourcesApi.run(workspaceId, sourceId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["runs", workspaceId] });
      qc.invalidateQueries({ queryKey: ["sources", workspaceId] });
    },
  });

  const toggleMut = useMutation({
    mutationFn: ({ src }: { src: ApiSource }) =>
      sourcesApi.update(workspaceId, src.id, {
        status: src.status === "active" ? "paused" : "active",
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["sources", workspaceId] }),
  });

  const deleteMut = useMutation({
    mutationFn: (sourceId: string) => sourcesApi.remove(workspaceId, sourceId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["sources", workspaceId] }),
  });

  return (
    <section className="panel p-5 space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <Rss className="h-5 w-5 text-primary shrink-0 mt-0.5" />
          <div>
            <div className="text-[14px] font-semibold">Continuous sources</div>
            <p className="text-[12px] text-muted-foreground mt-0.5">
              RSS feeds run articles through the LLM to extract entities + signals. REST API endpoints expect JSON in the export shape and apply it directly. A background poller wakes every minute and runs whichever sources are due. <strong>Run now</strong> to trigger one immediately.
            </p>
          </div>
        </div>
        {!adding && (
          <div className="flex gap-2 shrink-0">
            <button
              onClick={() => {
                setAdding("rss");
                setCreateError(null);
              }}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary text-primary-foreground px-3 py-1.5 text-[12px] font-semibold"
            >
              <Rss className="h-3.5 w-3.5" /> RSS
            </button>
            <button
              onClick={() => {
                setAdding("rest_api");
                setCreateError(null);
              }}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary text-primary-foreground px-3 py-1.5 text-[12px] font-semibold"
            >
              <Database className="h-3.5 w-3.5" /> REST API
            </button>
          </div>
        )}
      </div>

      {adding && (
        <div className="rounded-md border border-primary/40 bg-primary/5 p-3 space-y-3">
          <div className="flex items-center justify-between text-[11px] text-foreground/85">
            <div className="inline-flex items-center gap-1.5 font-semibold">
              {adding === "rss" ? <Rss className="h-3.5 w-3.5" /> : <Database className="h-3.5 w-3.5" />}
              New {adding === "rss" ? "RSS feed" : "REST API endpoint"}
            </div>
            <button onClick={reset} className="text-muted-foreground hover:text-foreground">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>

          <div>
            <div className="label-eyebrow !mb-1">Display name</div>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={adding === "rss" ? "e.g. El Tiempo · Colombia" : "e.g. Internal entity API"}
              className="w-full rounded border border-border bg-surface px-2 py-1.5 text-[12px] outline-none focus:border-primary/50"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-[1fr_120px] gap-2">
            <div>
              <div className="label-eyebrow !mb-1">{adding === "rss" ? "Feed URL" : "Endpoint URL"}</div>
              <input
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder={adding === "rss" ? "https://example.com/rss" : "https://api.example.com/horizon/export"}
                className="w-full rounded border border-border bg-surface px-2 py-1.5 text-[12px] outline-none focus:border-primary/50"
              />
            </div>
            <div>
              <div className="label-eyebrow !mb-1">Interval (min)</div>
              <input
                type="number"
                min={5}
                value={interval}
                onChange={(e) => setInterval(Math.max(5, Number(e.target.value) || 60))}
                className="w-full rounded border border-border bg-surface px-2 py-1.5 text-[12px] outline-none focus:border-primary/50"
              />
            </div>
          </div>

          {adding === "rss" && (
            <div className="grid grid-cols-1 md:grid-cols-[120px] gap-2">
              <div>
                <div className="label-eyebrow !mb-1">Max / poll</div>
                <input
                  type="number"
                  min={1}
                  max={100}
                  value={maxArticles}
                  onChange={(e) => setMaxArticles(Math.max(1, Number(e.target.value) || 25))}
                  className="w-full rounded border border-border bg-surface px-2 py-1.5 text-[12px] outline-none focus:border-primary/50"
                />
              </div>
            </div>
          )}

          {adding === "rest_api" && (
            <>
              <div className="grid grid-cols-[120px] gap-2">
                <div>
                  <div className="label-eyebrow !mb-1">HTTP method</div>
                  <select
                    value={method}
                    onChange={(e) => setMethod(e.target.value as "GET" | "POST")}
                    className="w-full rounded border border-border bg-surface px-2 py-1.5 text-[12px] outline-none focus:border-primary/50"
                  >
                    <option value="GET">GET</option>
                    <option value="POST">POST</option>
                  </select>
                </div>
              </div>
              <div>
                <div className="label-eyebrow !mb-1">Request headers (auth, API keys)</div>
                <div className="space-y-1.5">
                  {headers.map((h, i) => (
                    <div key={i} className="flex gap-1.5">
                      <input
                        value={h.key}
                        onChange={(e) =>
                          setHeaders((prev) =>
                            prev.map((p, j) => (j === i ? { ...p, key: e.target.value } : p)),
                          )
                        }
                        placeholder="Authorization"
                        className="w-[140px] rounded border border-border bg-surface px-2 py-1.5 text-[11px] outline-none focus:border-primary/50 font-mono"
                      />
                      <input
                        value={h.value}
                        onChange={(e) =>
                          setHeaders((prev) =>
                            prev.map((p, j) => (j === i ? { ...p, value: e.target.value } : p)),
                          )
                        }
                        placeholder="Bearer YOUR_TOKEN"
                        type={h.key.toLowerCase().includes("auth") || h.key.toLowerCase().includes("key") ? "password" : "text"}
                        className="flex-1 rounded border border-border bg-surface px-2 py-1.5 text-[11px] outline-none focus:border-primary/50 font-mono"
                      />
                      <button
                        onClick={() => setHeaders((prev) => prev.filter((_, j) => j !== i))}
                        disabled={headers.length === 1}
                        className="rounded border border-border bg-surface px-2 text-muted-foreground hover:text-foreground disabled:opacity-40"
                        title="Remove header"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                  <button
                    onClick={() => setHeaders((prev) => [...prev, { key: "", value: "" }])}
                    className="text-[11px] text-primary hover:underline"
                  >
                    + Add header
                  </button>
                </div>
                <p className="text-[10px] text-muted-foreground mt-1.5">
                  Common: <code>Authorization: Bearer …</code>, <code>X-API-Key: …</code>. Values are stored as-is — encrypt at rest before going to production.
                </p>
              </div>
              <p className="text-[11px] text-muted-foreground">
                Expected response: either an object with{" "}
                <code>{`{ entities, relationships, signals, scenarios }`}</code> arrays (same shape as Horizon's export), or a plain array of entity objects with <code>type</code> and <code>name</code>.
              </p>
            </>
          )}

          {createError && (
            <p className="text-[11px] text-risk-high inline-flex items-center gap-1.5">
              <AlertTriangle className="h-3 w-3" /> {createError}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <button
              onClick={reset}
              className="rounded border border-border bg-surface px-3 py-1.5 text-[12px] text-foreground/80 hover:bg-surface-elevated"
            >
              Cancel
            </button>
            <button
              onClick={() => createMut.mutate()}
              disabled={!name.trim() || !url.trim() || createMut.isPending}
              className="rounded bg-primary text-primary-foreground px-4 py-1.5 text-[12px] font-semibold disabled:opacity-50"
            >
              {createMut.isPending ? "Saving…" : "Add source"}
            </button>
          </div>
        </div>
      )}

      <div className="rounded-md border border-border bg-surface overflow-hidden">
        {(sources.data ?? []).length === 0 ? (
          <div className="px-4 py-6 text-center text-[12px] text-muted-foreground">
            No sources yet.
          </div>
        ) : (
          <table className="w-full text-[12px]">
            <thead className="text-[10px] uppercase tracking-wider text-muted-foreground bg-surface-elevated/50">
              <tr>
                <th className="text-left px-3 py-2">Source</th>
                <th className="text-left px-3 py-2">URL</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-left px-3 py-2">Last polled</th>
                <th className="text-right px-3 py-2">Interval</th>
                <th className="text-right px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {(sources.data ?? []).map((s) => (
                <tr key={s.id} className="border-t border-border align-top">
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-1.5 font-semibold text-foreground/95">
                      {s.kind === "rss" ? (
                        <Rss className="h-3 w-3 text-primary" />
                      ) : (
                        <Database className="h-3 w-3 text-primary" />
                      )}
                      {s.name}
                    </div>
                    {s.last_error && (
                      <div className="text-[10px] text-risk-high mt-0.5 line-clamp-2">
                        {s.last_error}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-muted-foreground font-mono text-[11px]">
                    <a
                      href={(s.config as { url?: string }).url}
                      target="_blank"
                      rel="noreferrer"
                      className="hover:text-foreground break-all"
                    >
                      {(s.config as { url?: string }).url}
                    </a>
                  </td>
                  <td className="px-3 py-2.5">
                    <StatusBadge status={s.status} />
                  </td>
                  <td className="px-3 py-2.5 text-muted-foreground">
                    {s.last_polled_at ? relativeTime(s.last_polled_at) : "—"}
                  </td>
                  <td className="px-3 py-2.5 text-right text-muted-foreground font-mono">
                    {s.poll_interval_minutes}m
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex justify-end gap-1.5">
                      <button
                        onClick={() => runMut.mutate(s.id)}
                        disabled={runMut.isPending}
                        className="inline-flex items-center gap-1 rounded border border-border bg-surface px-2 py-1 text-[11px] hover:bg-surface-elevated disabled:opacity-50"
                        title="Run now"
                      >
                        <Play className="h-3 w-3" /> Run
                      </button>
                      <button
                        onClick={() => toggleMut.mutate({ src: s })}
                        disabled={toggleMut.isPending}
                        className="inline-flex items-center gap-1 rounded border border-border bg-surface px-2 py-1 text-[11px] hover:bg-surface-elevated"
                        title={s.status === "active" ? "Pause" : "Resume"}
                      >
                        {s.status === "active" ? <Pause className="h-3 w-3" /> : <Play className="h-3 w-3" />}
                      </button>
                      <button
                        onClick={() => {
                          if (confirm(`Delete source "${s.name}"?`)) {
                            deleteMut.mutate(s.id);
                          }
                        }}
                        className="inline-flex items-center gap-1 rounded border border-risk-high/40 bg-risk-high/10 text-risk-high px-2 py-1 text-[11px] hover:bg-risk-high/20"
                        title="Delete source"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}

function RunsTable({ workspaceId }: { workspaceId: string }) {
  const runs = useQuery({
    queryKey: ["runs", workspaceId],
    queryFn: () => sourcesApi.runs(workspaceId),
    refetchInterval: 15_000,
  });
  const sources = useQuery({
    queryKey: ["sources", workspaceId],
    queryFn: () => sourcesApi.list(workspaceId),
  });
  const nameById = Object.fromEntries((sources.data ?? []).map((s) => [s.id, s.name]));

  const all = runs.data ?? [];
  if (all.length === 0) return null;

  return (
    <section className="panel overflow-hidden">
      <div className="px-5 py-3 border-b border-border flex items-center justify-between">
        <Eyebrow count={all.length}>Recent runs</Eyebrow>
        <span className="text-[10px] text-muted-foreground">auto-refresh 15s</span>
      </div>
      <table className="w-full text-[12px]">
        <thead className="text-[10px] uppercase tracking-wider text-muted-foreground bg-surface-elevated/40">
          <tr>
            <th className="text-left px-5 py-2">Source</th>
            <th className="text-left px-3 py-2">Started</th>
            <th className="text-left px-3 py-2">Status</th>
            <th className="text-right px-3 py-2">Articles</th>
            <th className="text-right px-3 py-2">Created</th>
            <th className="text-right px-5 py-2">Duration</th>
          </tr>
        </thead>
        <tbody>
          {all.slice(0, 25).map((r) => (
            <tr key={r.id} className="border-t border-border align-top">
              <td className="px-5 py-2.5">{nameById[r.source_id] ?? r.source_id.slice(0, 8)}</td>
              <td className="px-3 py-2.5 text-muted-foreground">{relativeTime(r.started_at)}</td>
              <td className="px-3 py-2.5">
                <RunStatusBadge run={r} />
                {r.error && (
                  <div className="text-[10px] text-risk-high mt-0.5 line-clamp-2 max-w-[280px]">
                    {r.error}
                  </div>
                )}
              </td>
              <td className="px-3 py-2.5 text-right font-mono text-muted-foreground">
                {r.articles_processed} / {r.articles_seen}
              </td>
              <td className="px-3 py-2.5 text-right font-mono text-foreground/80">
                {sumCreated(r)}
              </td>
              <td className="px-5 py-2.5 text-right text-muted-foreground font-mono">
                {duration(r)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function StatusBadge({ status }: { status: ApiSource["status"] }) {
  const map: Record<ApiSource["status"], { color: string; label: string }> = {
    active: { color: "text-risk-low", label: "Active" },
    paused: { color: "text-muted-foreground", label: "Paused" },
    error: { color: "text-risk-high", label: "Error" },
  };
  const cfg = map[status];
  return (
    <span className={`inline-flex items-center gap-1.5 text-[11px] ${cfg.color}`}>
      <Circle className="h-1.5 w-1.5 fill-current" /> {cfg.label}
    </span>
  );
}

function RunStatusBadge({ run }: { run: ApiIngestionRun }) {
  if (run.status === "running") {
    return (
      <span className="inline-flex items-center gap-1.5 text-[11px] text-primary">
        <Circle className="h-1.5 w-1.5 fill-current animate-pulse" /> Running
      </span>
    );
  }
  if (run.status === "succeeded") {
    return (
      <span className="inline-flex items-center gap-1.5 text-[11px] text-risk-low">
        <CheckCircle2 className="h-3 w-3" /> Succeeded
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] text-risk-high">
      <AlertTriangle className="h-3 w-3" /> Failed
    </span>
  );
}

function sumCreated(r: ApiIngestionRun): string {
  const c = r.counts ?? {};
  const e = c.entities_created ?? 0;
  const s = c.signals_created ?? 0;
  const rel = c.relationships_created ?? 0;
  return `${e}e / ${rel}r / ${s}s`;
}

function duration(r: ApiIngestionRun): string {
  if (!r.finished_at) return "—";
  const ms = new Date(r.finished_at).getTime() - new Date(r.started_at).getTime();
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60_000).toFixed(1)}m`;
}

function relativeTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return "just now";
  const mins = Math.round(ms / 60_000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  return `${days}d ago`;
}

function ComingSoonPanel({
  icon: Icon,
  title,
  description,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
}) {
  return (
    <section className="panel p-5 opacity-70">
      <div className="flex items-start gap-3">
        <Icon className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
        <div>
          <div className="flex items-center gap-2">
            <div className="text-[14px] font-semibold text-foreground/85">{title}</div>
            <span className="text-[9px] font-semibold tracking-wider uppercase text-muted-foreground bg-surface px-1.5 py-0.5 rounded border border-border">
              Coming soon
            </span>
          </div>
          <p className="text-[12px] text-muted-foreground mt-0.5">{description}</p>
        </div>
      </div>
    </section>
  );
}
